import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserRegistrationService from '../services/UserRegistrationService';
import ServiceHDSService from '../services/ServiceDHSService';
import { FaSpinner } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import PhoneNumberInput from "../utils/PhoneNumberInput";
import CountryInput from "../utils/CountryInput";
import CityInput from "../utils/CityInput";
import NeighborhoodInput from "../utils/NeighborhoodInput";
import { sanitizeNameInput, sanitizeEmailInput } from "../utils/sanitizers";
import {
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Switch,
    Checkbox,
    Button,
    Fade,
    InputAdornment,
    Typography,
    IconButton,
    Card,
    CardContent,
    Box,
    Stepper,
    Step,
    StepLabel,
    Chip,
    Divider,
    Alert,
    Collapse,
    Modal,
    CircularProgress
} from '@mui/material';
import {
    Home,
    Notes,
    Person,
    Email,
    CalendarToday,
    Wc,
    Lock,
    AccountCircle,
    Visibility,
    VisibilityOff,
    ArrowBack,
    ArrowForward,
    Send,
    Badge,
    Gavel,
    AttachFile,
    Groups,
    Work,
    Language as LanguageIcon,
    CameraAlt,
    Map as MapIcon,
    Description,
    Add,
    Delete,
    CheckCircle,
    Warning,
    Info,
    Cancel
} from '@mui/icons-material';
import LoadingSpinner from "../utils/LoadingSpinner";

const CLIENT_PROFILE = 'je cherche un aide menager';
const PROFESSIONAL_PROFILE = 'je propose mes service';

const CLIENT_DOCUMENT_TYPE_OPTIONS = [
    { value: 'CNI', label: "Carte d'identité nationale" },
    { value: 'PASSPORT', label: 'Passeport' },
    { value: 'DRIVING_LICENSE', label: 'Permis de conduire' },
    { value: 'RESIDENCE_PERMIT', label: 'Titre de séjour' },
];

const PROFESSIONAL_DOCUMENT_TYPE_OPTIONS = [
    { value: 'CNI', label: "Carte nationale d'identité" },
    { value: 'PASSPORT', label: 'Passeport' },
    { value: 'DRIVING_LICENSE', label: 'Permis de conduire' },
];

const AVAILABLE_LANGUAGES = ["Anglais", "Français", "Espagnol", "Chinois (Mandarin)", "Arabe"];

const YEARS_OF_EXPERIENCE_OPTIONS = Array.from({ length: 61 }, (_, i) => i);

// ✅ Définition stricte des types de fichiers acceptés
const ACCEPTED_IMAGE_ATTR = 'image/jpeg,image/jpg,image/png,image/webp';
const ACCEPTED_DOC_ATTR = 'image/jpeg,image/jpg,image/png,image/webp,application/pdf';
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ACCEPTED_DOC_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];

const getPasswordRules = (password = '') => {
    const rules = [];
    if (password.length < 6) rules.push("au moins 6 caractères");
    if (!/[0-9]/.test(password)) rules.push("un chiffre");
    if (!/[#,@,%,!]/.test(password)) rules.push("un caractère spécial (#,@,%,!)");
    if (!/[A-Z]/.test(password)) rules.push("une lettre majuscule");
    if (!/[a-z]/.test(password)) rules.push("une lettre minuscule");
    return rules;
};

const cardSx = {
    background: '#ffffff',
    borderRadius: '18px',
    padding: { xs: '18px', sm: '24px', md: '28px' },
    boxShadow: '0 4px 20px rgba(16,24,40,0.07)',
    border: '1px solid rgba(16,24,40,0.04)',
};

const sectionHeaderSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #eef1f6',
    flexWrap: 'wrap',
};

const sectionIconSx = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 42, height: 42, borderRadius: '13px', flexShrink: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff', boxShadow: '0 4px 12px rgba(102,126,234,0.35)',
};

const requiredChipSx = {
    background: '#fef2f2', color: '#e53935', fontWeight: 700,
    fontSize: 11, letterSpacing: '0.4px', height: 24,
};

const optionalChipSx = {
    background: '#f1f5f9', color: '#64748b', fontWeight: 700,
    fontSize: 11, letterSpacing: '0.4px', height: 24,
};

const zoneBase = {
    border: '1.5px dashed #94a3b8',
    borderRadius: '14px',
    padding: '20px',
    background: '#fafbfc',
};
const zoneValid = {
    border: '1.5px solid #34a853',
    borderRadius: '14px',
    padding: '20px',
    background: '#f3faf4',
    boxShadow: '0 2px 10px rgba(52,168,83,0.14)',
};
const zoneMissing = {
    border: '1.5px dashed #e53935',
    borderRadius: '14px',
    padding: '20px',
    background: '#fff5f5',
};

const SectionCard = ({ icon, title, required, optional, children }) => (
    <Box sx={cardSx}>
        <Box sx={sectionHeaderSx}>
            <Box sx={sectionIconSx}>{icon}</Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', flex: 1, minWidth: 120 }}>
                {title}
            </Typography>
            {required && <Chip label="Obligatoire" size="small" sx={requiredChipSx} />}
            {optional && <Chip label="Facultatif" size="small" sx={optionalChipSx} />}
        </Box>
        {children}
    </Box>
);

const initialFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    neighborhood: '',
    dateOfBirth: '',
    gender: 'M',
    city: '',
    country: '',
    additionalInfos: '',
    username: '',
    password: '',
    passwordConfirm: '',
    profileType: '',

    profilePhoto: null,
    profilePhotoPreview: null,

    identityDocumentType: '',
    identityDocument: null,
    identityDocumentPreview: null,

    selectedService: null,
    serviceId: null,
    subServicesSelected: [],
    languages: [],
    availabilityStatus: true,
    yearsOfExperience: '',
    about: '',
    contact1Name: '',
    contact1Phone: '',
    contact1Relationship: '',
    contact2Name: '',
    contact2Phone: '',
    contact2Relationship: '',
    cv: null,

    wantsCriminalRecord: false,
    identificationDocumentType: '',
    identificationDocumentName: '',
    identificationIssueDate: '',
    identificationExpiryDate: '',
    identificationIssuingAuthority: '',
    identificationCountry: '',
    identificationFrontDoc: null,
    identificationFrontDocPreview: null,
    identificationBackDoc: null,
    identificationBackDocPreview: null,
    locationPlan: null,

    criminalRecordDocumentName: 'Casier judiciaire',
    criminalRecordIssueDate: '',
    criminalRecordExpiryDate: '',
    criminalRecordIssuingAuthority: '',
    criminalRecordCountry: '',
    criminalRecordFrontDoc: null,
    criminalRecordFrontDocPreview: null,
    criminalRecordBackDoc: null,
    criminalRecordBackDocPreview: null,
};

const RegisterPage = () => {
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

    const [servicesData, setServicesData] = useState({});
    const [servicesLoading, setServicesLoading] = useState(true);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [selectedServiceKey, setSelectedServiceKey] = useState(null);
    const [selectedServiceIdTmp, setSelectedServiceIdTmp] = useState(null);
    const [selectedSubServicesTmp, setSelectedSubServicesTmp] = useState([]);

    const isProfessional = formData.profileType === PROFESSIONAL_PROFILE;

    const steps = isProfessional
        ? ['Type de Profil', 'Informations Personnelles', 'Adresse', 'Détails Professionnels', 'Documents', 'Connexion']
        : ['Type de Profil', 'Informations Personnelles', 'Adresse', 'Identification', 'Connexion'];

    const lastStep = steps.length - 1;
    const stepLabel = steps[step];

    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 3000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const fetchServices = async () => {
            try {
                setServicesLoading(true);
                const services = await ServiceHDSService.getAllServices();
                const transformed = {};
                (Array.isArray(services) ? services : []).forEach(s => {
                    transformed[s.name] = {
                        id: s.id,
                        name: s.name,
                        icon: s.mediaUrlPresigned || s.mediaUrl || '/assets/images/icon/default.png',
                        subServices: (s.subServices || []).map(sub => sub.name),
                    };
                });
                setServicesData(transformed);
            } catch (error) {
                console.error('Erreur lors du chargement des services:', error);
            } finally {
                setServicesLoading(false);
            }
        };
        fetchServices();
    }, []);

    const resetForm = () => {
        setFormData(initialFormData);
        setErrors({});
        setTouched({});
        setStep(0);
        setIsSubmitting(false);
    };

    const validateForm = (values, label) => {
        let errors = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (label === 'Type de Profil') {
            if (!values.profileType) errors.profileType = 'Le type de profil est requis';
        }

        if (label === 'Informations Personnelles') {
            if (!values.firstName.trim()) {
                errors.firstName = 'Le prénom est requis';
            } else if (values.firstName.length < 2) {
                errors.firstName = 'Le prénom doit avoir au moins 2 caractères';
            }
            if (!values.lastName.trim()) {
                errors.lastName = 'Le nom est requis';
            } else if (values.lastName.length < 2) {
                errors.lastName = 'Le nom doit avoir au moins 2 caractères';
            }
            if (!values.email) {
                errors.email = 'L\'email est requis';
            } else if (!/\S+@\S+\.\S+/.test(values.email)) {
                errors.email = 'L\'email est invalide';
            }
            if (!values.phoneNumber) errors.phoneNumber = 'Le numéro de téléphone est requis';
            if (!values.dateOfBirth) {
                errors.dateOfBirth = 'La date de naissance est requise';
            } else {
                const birthDate = new Date(values.dateOfBirth);
                if (birthDate > today) errors.dateOfBirth = 'La date de naissance ne peut pas être dans le futur';
            }
            if (!values.gender) errors.gender = 'Le genre est requis';
        }

        if (label === 'Adresse') {
            if (!values.neighborhood.trim()) {
                errors.neighborhood = 'Le quartier est requis';
            } else if (values.neighborhood.length < 2) {
                errors.neighborhood = 'Le quartier doit avoir au moins 2 caractères';
            }
            if (!values.city.trim()) errors.city = 'La ville est requise';
            if (!values.country.trim()) errors.country = 'Le pays est requis';
        }

        if (label === 'Identification') {
            if (!values.profilePhoto && !values.profilePhotoPreview) errors.profilePhoto = 'La photo de profil est requise';
            if (!values.identityDocumentType) errors.identityDocumentType = 'Le type de pièce d\'identité est requis';
            if (!values.identityDocument) errors.identityDocument = 'Le document d\'identité est requis';
        }

        if (label === 'Détails Professionnels') {
            if (!values.selectedService || !values.serviceId) errors.serviceId = 'Le service est requis';
            if (!values.languages || values.languages.length === 0) errors.languages = 'Au moins une langue est requise';
            if (values.yearsOfExperience === '' || values.yearsOfExperience === null) errors.yearsOfExperience = 'Les années d\'expérience sont requises';
            if (!values.about || !values.about.trim()) errors.about = 'La description "à propos de vous" est requise';
            if (!values.contact1Name || !values.contact1Name.trim()) errors.contact1Name = 'Le nom du contact 1 est requis';
            if (!values.contact1Phone || !values.contact1Phone.trim()) errors.contact1Phone = 'Le téléphone du contact 1 est requis';
            if (!values.contact2Name || !values.contact2Name.trim()) errors.contact2Name = 'Le nom du contact 2 est requis';
            if (!values.contact2Phone || !values.contact2Phone.trim()) errors.contact2Phone = 'Le téléphone du contact 2 est requis';
            if (!values.cv) errors.cv = 'Le CV est requis';
        }

        if (label === 'Documents') {
            if (!values.profilePhoto && !values.profilePhotoPreview) errors.profilePhoto = 'La photo de profil est requise';
            if (!values.cv) errors.cv = 'Le CV est requis';

            if (!values.identificationDocumentType) errors.identificationDocumentType = 'Le type de document est requis';
            if (!values.identificationFrontDoc) errors.identificationFrontDoc = 'Le recto du document d\'identité est requis';
            if (!values.locationPlan) errors.locationPlan = 'Le plan de localisation est requis';

            if (!values.identificationIssueDate) {
                errors.identificationIssueDate = 'La date d\'émission est requise';
            } else {
                const idIssueDate = new Date(values.identificationIssueDate);
                if (idIssueDate > today) {
                    errors.identificationIssueDate = 'La date de délivrance ne peut pas être dans le futur';
                } else if (values.identificationExpiryDate) {
                    const idExpiryDate = new Date(values.identificationExpiryDate);
                    if (idExpiryDate <= idIssueDate) {
                        errors.identificationExpiryDate = 'La date d\'expiration doit être postérieure à la date de délivrance';
                    }
                }
            }

            if (!values.identificationCountry || !values.identificationCountry.trim()) errors.identificationCountry = 'Le pays est requis';

            if (values.wantsCriminalRecord) {
                if (!values.criminalRecordIssueDate) {
                    errors.criminalRecordIssueDate = 'La date d\'émission est requise';
                } else {
                    const crIssueDate = new Date(values.criminalRecordIssueDate);
                    if (crIssueDate > today) {
                        errors.criminalRecordIssueDate = 'La date de délivrance ne peut pas être dans le futur';
                    } else if (values.criminalRecordExpiryDate) {
                        const crExpiryDate = new Date(values.criminalRecordExpiryDate);
                        if (crExpiryDate <= crIssueDate) {
                            errors.criminalRecordExpiryDate = 'La date d\'expiration doit être postérieure à la date de délivrance';
                        }
                    }
                }

                if (!values.criminalRecordExpiryDate) errors.criminalRecordExpiryDate = errors.criminalRecordExpiryDate || 'La date d\'expiration est requise';
                if (!values.criminalRecordIssuingAuthority || !values.criminalRecordIssuingAuthority.trim()) errors.criminalRecordIssuingAuthority = 'L\'autorité émettrice est requise';
                if (!values.criminalRecordCountry || !values.criminalRecordCountry.trim()) errors.criminalRecordCountry = 'Le pays est requis';
                if (!values.criminalRecordFrontDoc) errors.criminalRecordFrontDoc = 'Le recto du document est requis';
            }
        }

        if (label === 'Connexion') {
            if (!values.username.trim()) {
                errors.username = 'Le nom d\'utilisateur est requis';
            } else if (values.username.length < 3) {
                errors.username = 'Le nom d\'utilisateur doit avoir au moins 3 caractères';
            } else if (/\s/.test(values.username)) {
                errors.username = 'Le nom d\'utilisateur ne doit pas contenir d\'espaces';
            }
            if (!values.password) {
                errors.password = 'Le mot de passe est requis';
            } else {
                const unmetRules = getPasswordRules(values.password);
                if (unmetRules.length > 0) {
                    errors.password = 'Le mot de passe ne respecte pas les critères requis';
                }
            }
            if (!values.passwordConfirm) {
                errors.passwordConfirm = 'La confirmation du mot de passe est requise';
            } else if (values.passwordConfirm !== values.password) {
                errors.passwordConfirm = 'Les mots de passe ne correspondent pas';
            }
        }

        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let sanitizedValue = value;
        if (['firstName', 'lastName', 'neighborhood'].includes(name)) {
            sanitizedValue = sanitizeNameInput(value);
        } else if (name === 'email') {
            sanitizedValue = sanitizeEmailInput(value);
        }
        setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
        if (touched[name]) {
            const validationErrors = validateForm({ ...formData, [name]: sanitizedValue }, stepLabel);
            setErrors(prev => ({ ...prev, [name]: validationErrors[name] }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        const validationErrors = validateForm(formData, stepLabel);
        setErrors(prev => ({ ...prev, [name]: validationErrors[name] }));
    };

    const handleFileChange = (fieldName, file, imageOnly = false) => {
        const previewField = `${fieldName}Preview`;
        const prevPreview = formData[previewField];
        if (prevPreview) URL.revokeObjectURL(prevPreview);

        if (!file) {
            setFormData(prev => ({ ...prev, [fieldName]: null, [previewField]: null }));
            return;
        }

        // ✅ Vérification stricte du type de fichier
        if (imageOnly) {
            if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
                toast.error('Seules les images (JPEG, JPG, PNG, WEBP) sont acceptées pour ce champ.');
                return;
            }
        } else {
            if (!ACCEPTED_DOC_TYPES.includes(file.type)) {
                toast.error('Format invalide. Seuls les fichiers PDF et les images (JPEG, PNG, WEBP) sont autorisés.');
                return;
            }
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('La taille maximale autorisée est de 10 MB.');
            return;
        }

        const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
        setFormData(prev => ({ ...prev, [fieldName]: file, [previewField]: preview }));
    };

    const selectProfileType = (type) => {
        setFormData(prev => ({ ...prev, profileType: type }));
        setStep(1);
    };

    const handleIdentificationTypeChange = (e) => {
        const value = e.target.value;
        const opt = PROFESSIONAL_DOCUMENT_TYPE_OPTIONS.find(o => o.value === value);
        setFormData(prev => ({
            ...prev,
            identificationDocumentType: value,
            identificationDocumentName: opt ? opt.label : '',
        }));
    };

    const nextStep = () => {
        const validationErrors = validateForm(formData, stepLabel);
        if (Object.keys(validationErrors).length === 0) {
            setStep(step + 1);
            setErrors({});
        } else {
            setErrors(validationErrors);
            toast.error('Veuillez corriger les erreurs avant de continuer', { toastId: 'form-errors' });
        }
    };

    const prevStep = () => {
        if (isSubmitting) return;
        setStep(step - 1);
    };

    const formatDateForBackend = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year} 00:00`;
        } catch (error) {
            return null;
        }
    };

    const formatDateOnlyForBackend = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        } catch (error) {
            return null;
        }
    };

    const convertGenderForBackend = (gender) => {
        const genderMap = { 'M': 'MALE', 'F': 'FEMALE' };
        return genderMap[gender] || '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm(formData, stepLabel);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error('Veuillez corriger les erreurs avant de soumettre', { toastId: 'submit-errors' });
            return;
        }

        setIsSubmitting(true);
        const toastId = toast.loading("Création du compte en cours...", { position: "top-center" });

        try {
            const sanitizedUsername = formData.username.toLowerCase();
            const roleIds = formData.profileType === CLIENT_PROFILE ? [3] : [4];

            const userData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                neighborhood: formData.neighborhood,
                dateOfBirth: formatDateForBackend(formData.dateOfBirth),
                createdBy: sanitizedUsername,
                gender: convertGenderForBackend(formData.gender),
                city: formData.city,
                country: formData.country,
                username: sanitizedUsername,
                password: formData.password,
                additionalInfos: formData.additionalInfos,
                roleIds,
                habilitationNames: [
                    "USER_CREATE", "USER_READ", "ROLE_DELETE",
                    "ROLE_UPDATE", "ROLE_READ", "ROLE_CREATE"
                ]
            };

            if (isProfessional) {
                const professionalData = {
                    serviceId: formData.serviceId ? Number(formData.serviceId) : null,
                    selectedService: formData.selectedService,
                    subServices: formData.subServicesSelected.join(', '),
                    languages: formData.languages.join(', '),
                    availabilityStatus: formData.availabilityStatus,
                    city: formData.city,
                    address: formData.neighborhood,
                    yearsOfExperience: formData.yearsOfExperience !== '' ? Number(formData.yearsOfExperience) : null,
                    about: formData.about,
                    contact1Name: formData.contact1Name,
                    contact1Phone: formData.contact1Phone,
                    contact1Relationship: formData.contact1Relationship,
                    contact2Name: formData.contact2Name,
                    contact2Phone: formData.contact2Phone,
                    contact2Relationship: formData.contact2Relationship,
                    createdBy: sanitizedUsername,
                    lastModifiedBy: sanitizedUsername,
                    //identificationSubmitted: true,
                    identificationSubmitted: formData.wantsCriminalRecord,
                    identification: {
                        documentType: formData.identificationDocumentType,
                        documentName: formData.identificationDocumentName,
                        issueDate: formatDateOnlyForBackend(formData.identificationIssueDate),
                        expiryDate: formatDateOnlyForBackend(formData.identificationExpiryDate),
                        issuingAuthority: formData.identificationIssuingAuthority,
                        country: formData.identificationCountry,
                    },
                    criminalRecord: formData.wantsCriminalRecord ? {
                        documentName: 'Casier judiciaire',
                        issueDate: formatDateOnlyForBackend(formData.criminalRecordIssueDate),
                        expiryDate: formatDateOnlyForBackend(formData.criminalRecordExpiryDate),
                        issuingAuthority: formData.criminalRecordIssuingAuthority,
                        country: formData.criminalRecordCountry,
                    } : null,
                };

                professionalData.status = formData.wantsCriminalRecord ? 'PENDING' : null;

                await UserRegistrationService.registerProfessional({
                    userData,
                    professionalData,
                    profilePhoto: formData.profilePhoto,
                    identificationFrontDoc: formData.identificationFrontDoc,
                    identificationBackDoc: formData.identificationBackDoc,
                    criminalRecordFrontDoc: formData.wantsCriminalRecord ? formData.criminalRecordFrontDoc : null,
                    criminalRecordBackDoc: formData.wantsCriminalRecord ? formData.criminalRecordBackDoc : null,
                    cv: formData.cv,
                    locationPlan: formData.locationPlan,
                });
            } else {
                const clientData = {
                    identityDocumentType: formData.identityDocumentType,
                    createdBy: sanitizedUsername,
                    lastModifiedBy: sanitizedUsername,
                };

                await UserRegistrationService.registerClient({
                    userData,
                    clientData,
                    profilePhoto: formData.profilePhoto,
                    identityDocument: formData.identityDocument,
                });
            }

            toast.update(toastId, {
                render: "Compte créé avec succès! Redirection...",
                type: "success",
                isLoading: false,
                autoClose: 2000
            });

            setTimeout(() => {
                resetForm();
                navigate('/login');
            }, 2000);

        } catch (error) {
            console.error('Erreur lors de la création:', error);
            let errorMessage = "Erreur lors de la création du compte";
            if (error.response) {
                errorMessage = error.response.data?.message || errorMessage;
            } else if (error.request) {
                errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
            }
            toast.update(toastId, { render: errorMessage, type: "error", isLoading: false, autoClose: 5000 });
        } finally {
            setIsSubmitting(false);
        }
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            '&:hover fieldset': { borderColor: '#153247' },
            '&.Mui-focused fieldset': { borderColor: '#153247' },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: '#153247' },
    };

    const renderFileUploadZone = (fieldName, label, { accept = ACCEPTED_DOC_ATTR, required = false, imageOnly = false } = {}) => {
        const file = formData[fieldName];
        const preview = formData[`${fieldName}Preview`];
        const hasFile = !!file;
        const zoneStyle = required ? (hasFile ? zoneValid : zoneMissing) : zoneBase;

        // ✅ Texte indicatif pour les formats acceptés
        const acceptedFormatsText = imageOnly
            ? "Formats acceptés : JPG, PNG, WEBP"
            : "Formats acceptés : PDF, JPG, PNG, WEBP";

        return (
            <Box sx={zoneStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                        {label}
                        {required && <span style={{ color: '#e53935', marginLeft: 4 }}>*</span>}
                        {!required && <span style={{ color: '#999', marginLeft: 4, fontSize: '11px' }}>(optionnel)</span>}
                    </Typography>
                    {required && (
                        hasFile
                            ? <Box sx={{ color: '#1e7e34', fontWeight: 600, fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '5px', background: '#34a85318', px: 1.2, py: 0.3, borderRadius: '999px' }}>
                                <CheckCircle sx={{ fontSize: 15 }} /> Fourni
                            </Box>
                            : <Box sx={{ color: '#e53935', fontWeight: 600, fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '5px', background: '#e5393514', px: 1.2, py: 0.3, borderRadius: '999px' }}>
                                <Warning sx={{ fontSize: 15 }} /> Manquant
                            </Box>
                    )}
                </Box>

                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5, fontSize: '11px', fontStyle: 'italic' }}>
                    {acceptedFormatsText} - Taille max: 10MB
                </Typography>

                {hasFile ? (
                    <Box sx={{ textAlign: 'center' }}>
                        {preview ? (
                            <img src={preview} alt="Aperçu" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.12)', display: 'inline-block' }} />
                        ) : (
                            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                <Description sx={{ fontSize: 56, color: 'grey.400' }} />
                                <Typography variant="caption" color="textSecondary">{file.name}</Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1.5, flexWrap: 'wrap' }}>
                            <Button variant="outlined" component="label" size="small" sx={{ borderRadius: '999px' }} disabled={isSubmitting}>
                                Remplacer
                                <input type="file" hidden accept={accept} onChange={(e) => handleFileChange(fieldName, e.target.files[0] || null, imageOnly)} />
                            </Button>
                            <Button variant="outlined" color="error" size="small" sx={{ borderRadius: '999px' }} disabled={isSubmitting} onClick={() => handleFileChange(fieldName, null, imageOnly)}>
                                Supprimer
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Button
                        fullWidth
                        variant={required ? 'contained' : 'outlined'}
                        component="label"
                        startIcon={imageOnly ? <CameraAlt /> : <AttachFile />}
                        color={required ? 'primary' : 'inherit'}
                        disabled={isSubmitting}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                    >
                        Charger {label.toLowerCase()}
                        <input type="file" hidden accept={accept} onChange={(e) => handleFileChange(fieldName, e.target.files[0] || null, imageOnly)} />
                    </Button>
                )}
            </Box>
        );
    };

    const navButtons = (onNext, nextLabel = 'Suivant', nextIcon = <ArrowForward />) => (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
                variant="outlined"
                onClick={prevStep}
                disabled={isSubmitting}
                startIcon={<ArrowBack />}
                sx={{
                    borderColor: '#ff6347', color: '#ff6347',
                    '&:hover': { backgroundColor: '#ff6347', color: 'white', borderColor: '#ff4500' },
                    minWidth: '120px', borderRadius: '25px'
                }}
            >
                Retour
            </Button>
            <Button
                variant="contained"
                onClick={onNext}
                disabled={isSubmitting}
                endIcon={isSubmitting ? null : nextIcon}
                sx={{
                    backgroundColor: nextLabel === 'Soumettre' ? '#1d8d36' : '#f8b400',
                    '&:hover': { backgroundColor: nextLabel === 'Soumettre' ? 'rgba(12,118,237,0.78)' : '#e6a200' },
                    minWidth: '150px', borderRadius: '25px'
                }}
            >
                {isSubmitting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '18px' }} />
                        Envoi en cours...
                    </Box>
                ) : nextLabel}
            </Button>
        </Box>
    );

    const renderProfileTypeStep = () => (
        <Fade in timeout={1000}>
            <div style={{ textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom style={{ marginBottom: '30px', fontWeight: 'bold' }}>
                    Quel est votre besoin ?
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
                    <Card
                        onClick={() => selectProfileType(CLIENT_PROFILE)}
                        sx={{
                            cursor: 'pointer', transition: 'all 0.3s ease',
                            border: formData.profileType === CLIENT_PROFILE ? '2px solid #1976d2' : '2px solid transparent',
                            '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 8px 16px rgba(0,0,0,0.2)', border: '2px solid #1976d2' }
                        }}
                    >
                        <CardContent>
                            <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                                <img src='/assets/images/icon/cl-2.png' alt="Logo" style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                     onError={(e) => { e.target.src = '/assets/images/icon/cl-2.png'; }} />
                                <Box style={{ textAlign: 'center' }}>
                                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '5px' }}>UNE AIDE</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Une baby-sitter, nounou, femme de ménage, prof particulier, aide à domicile, ...
                                    </Typography>
                                    <Typography variant="body1" style={{ fontWeight: 'bold', marginTop: '10px', color: '#1976d2' }}>
                                        J'ai besoin d'une aide
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        onClick={() => selectProfileType(PROFESSIONAL_PROFILE)}
                        sx={{
                            cursor: 'pointer', transition: 'all 0.3s ease',
                            border: formData.profileType === PROFESSIONAL_PROFILE ? '2px solid #1976d2' : '2px solid transparent',
                            '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 8px 16px rgba(0,0,0,0.2)', border: '2px solid #1976d2' }
                        }}
                    >
                        <CardContent>
                            <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                                <img src='/assets/images/icon/job.png' alt="Logo" style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                     onError={(e) => { e.target.src = '/assets/images/icon/job.png'; }} />
                                <Box style={{ textAlign: 'center' }}>
                                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '5px' }}>UN JOB</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        En tant que baby-sitter, nounou, femme de ménage, pet-sitter, aide à domicile, prof, ...
                                    </Typography>
                                    <Typography variant="body1" style={{ fontWeight: 'bold', marginTop: '10px', color: '#1976d2' }}>
                                        J'ai besoin d'un emploi
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </div>
        </Fade>
    );

    const renderPersonalStep = () => (
        <Fade in timeout={1000}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                        fullWidth
                        label={<>Prénom <span style={{ color: 'red' }}>*</span></>}
                        name="firstName" value={formData.firstName} onChange={handleChange} onBlur={handleBlur}
                        error={!!errors.firstName} helperText={errors.firstName} disabled={isSubmitting} autoComplete="off"
                        InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: '#153247' }} /></InputAdornment> }}
                        sx={fieldSx}
                    />
                    <TextField
                        fullWidth
                        label={<>Nom <span style={{ color: 'red' }}>*</span></>}
                        name="lastName" value={formData.lastName} onChange={handleChange} onBlur={handleBlur}
                        error={!!errors.lastName} helperText={errors.lastName} disabled={isSubmitting} autoComplete="off"
                        InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: '#153247' }} /></InputAdornment> }}
                        sx={fieldSx}
                    />
                </Box>

                <TextField
                    fullWidth
                    label={<>Email <span style={{ color: 'red' }}>*</span></>}
                    name="email" type="email" value={formData.email} onChange={handleChange} onBlur={handleBlur}
                    error={!!errors.email} helperText={errors.email} disabled={isSubmitting} autoComplete="off"
                    InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: '#153247' }} /></InputAdornment> }}
                    sx={fieldSx}
                />

                <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                        Numéro de téléphone <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <PhoneNumberInput
                        value={formData.phoneNumber}
                        onChange={(value) => setFormData(prev => ({ ...prev, phoneNumber: value }))}
                        onValidation={(error) => setErrors(prev => ({ ...prev, phoneNumber: error }))}
                        disabled={isSubmitting}
                        defaultCountry="CM"
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                        fullWidth
                        label={<>Date de naissance <span style={{ color: 'red' }}>*</span></>}
                        name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} onBlur={handleBlur}
                        error={!!errors.dateOfBirth} helperText={errors.dateOfBirth} disabled={isSubmitting} autoComplete="off"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday sx={{ color: '#153247' }} /></InputAdornment> }}
                        sx={fieldSx}
                    />
                    <FormControl fullWidth error={!!errors.gender}>
                        <InputLabel>Genre <span style={{ color: 'red' }}>*</span></InputLabel>
                        <Select
                            name="gender" value={formData.gender} onChange={handleChange} onBlur={handleBlur} disabled={isSubmitting}
                            label={<>Genre <span style={{ color: 'red' }}>*</span></>}
                            startAdornment={<InputAdornment position="start"><Wc sx={{ color: '#153247', ml: 1 }} /></InputAdornment>}
                        >
                            <MenuItem value="M">Homme</MenuItem>
                            <MenuItem value="F">Femme</MenuItem>
                        </Select>
                        {errors.gender && <Typography color="error" variant="caption" sx={{ ml: 2 }}>{errors.gender}</Typography>}
                    </FormControl>
                </Box>

                {navButtons(nextStep)}
            </Box>
        </Fade>
    );

    const renderAddressStep = () => (
        <Fade in timeout={1000}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <CountryInput
                    value={formData.country}
                    onChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                    onValidation={(error) => setErrors(prev => ({ ...prev, country: error }))}
                    disabled={isSubmitting}
                    required={true}
                />
                <CityInput
                    value={formData.city}
                    onChange={(value) => setFormData(prev => ({ ...prev, city: value }))}
                    onValidation={(error) => setErrors(prev => ({ ...prev, city: error }))}
                    disabled={isSubmitting}
                    required={true}
                />
                <NeighborhoodInput
                    value={formData.neighborhood}
                    onChange={(value) => setFormData(prev => ({ ...prev, neighborhood: value }))}
                    onValidation={(error) => setErrors(prev => ({ ...prev, neighborhood: error }))}
                    disabled={isSubmitting}
                    required={true}
                />

                {!isProfessional && (
                    <TextField
                        fullWidth
                        label="Informations complémentaires (facultatif)"
                        name="additionalInfos" value={formData.additionalInfos} onChange={handleChange} onBlur={handleBlur}
                        multiline rows={4} disabled={isSubmitting} autoComplete="off"
                        InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}><Notes sx={{ color: '#153247' }} /></InputAdornment> }}
                        sx={fieldSx}
                    />
                )}

                {navButtons(nextStep)}
            </Box>
        </Fade>
    );

    const renderClientIdentificationStep = () => (
        <Fade in timeout={1000}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Alert severity="info">
                    Votre photo de profil et votre pièce d'identité sont nécessaires pour créer votre compte.
                </Alert>

                <SectionCard icon={<CameraAlt />} title="Photo de profil" required>
                    {renderFileUploadZone('profilePhoto', 'Photo de profil', { accept: ACCEPTED_IMAGE_ATTR, required: true, imageOnly: true })}
                    {errors.profilePhoto && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.profilePhoto}</Typography>}
                </SectionCard>

                <SectionCard icon={<Badge />} title="Pièce d'identité" required>
                    <FormControl fullWidth error={!!errors.identityDocumentType} sx={{ mb: 3 }}>
                        <InputLabel>Type de pièce d'identité</InputLabel>
                        <Select
                            name="identityDocumentType" value={formData.identityDocumentType} onChange={handleChange} onBlur={handleBlur}
                            disabled={isSubmitting} label="Type de pièce d'identité"
                        >
                            {CLIENT_DOCUMENT_TYPE_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                        {errors.identityDocumentType && <Typography color="error" variant="caption" sx={{ ml: 2 }}>{errors.identityDocumentType}</Typography>}
                    </FormControl>

                    {renderFileUploadZone('identityDocument', "Document d'identité", { accept: ACCEPTED_DOC_ATTR, required: true })}
                    {errors.identityDocument && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.identityDocument}</Typography>}
                </SectionCard>

                {navButtons(nextStep)}
            </Box>
        </Fade>
    );

    const handleOpenServiceModal = () => setShowServiceModal(true);
    const handleCloseServiceModal = () => {
        setShowServiceModal(false);
        setSelectedServiceKey(null);
        setSelectedServiceIdTmp(null);
        setSelectedSubServicesTmp([]);
    };
    const handleSelectServiceKey = (key, id) => {
        setSelectedServiceKey(key);
        setSelectedServiceIdTmp(id);
        setSelectedSubServicesTmp([]);
    };
    const handleSubServiceToggle = (sub) => {
        setSelectedSubServicesTmp(prev => prev.includes(sub) ? prev.filter(s => s !== sub) : [...prev, sub]);
    };
    const handleSaveServiceSelection = () => {
        if (selectedServiceKey && selectedSubServicesTmp.length > 0) {
            setFormData(prev => ({
                ...prev,
                selectedService: selectedServiceKey,
                serviceId: selectedServiceIdTmp,
                subServicesSelected: selectedSubServicesTmp,
            }));
            handleCloseServiceModal();
        }
    };
    const handleRemoveSelectedService = () => {
        setFormData(prev => ({ ...prev, selectedService: null, serviceId: null, subServicesSelected: [] }));
    };

    const renderServiceSelectionModal = () => (
        <Modal open={showServiceModal} onClose={handleCloseServiceModal}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                width: { xs: '94vw', sm: 600, md: 800 }, maxWidth: '94vw',
                bgcolor: 'background.paper', boxShadow: 24, borderRadius: '18px',
                p: { xs: 2.5, sm: 4 }, maxHeight: '90vh', overflow: 'auto',
            }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>Sélectionnez un service</Typography>
                {servicesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                        <CircularProgress /><Typography sx={{ ml: 2 }}>Chargement des services...</Typography>
                    </Box>
                ) : !selectedServiceKey ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
                        {Object.entries(servicesData).map(([key, service]) => (
                            <Card
                                key={key}
                                sx={{
                                    cursor: 'pointer', borderRadius: '14px', transition: 'all 0.25s',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 22px rgba(16,24,40,0.12)' },
                                }}
                                onClick={() => handleSelectServiceKey(key, service.id)}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <img src={service.icon} alt={service.name} style={{ height: 70, marginBottom: '1rem' }}
                                         onError={(e) => { e.target.src = '/assets/images/icon/default.png'; }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.15rem' } }}>{service.name}</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5, flexWrap: 'wrap' }}>
                            <img src={servicesData[selectedServiceKey]?.icon} alt={servicesData[selectedServiceKey]?.name} style={{ height: 52 }}
                                 onError={(e) => { e.target.src = '/assets/images/icon/default.png'; }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {servicesData[selectedServiceKey]?.name} — Sélectionnez une ou plusieurs compétences
                            </Typography>
                        </Box>
                        <FormControl component="fieldset" fullWidth>
                            <FormLabel component="legend">Compétences disponibles</FormLabel>
                            <FormGroup>
                                {servicesData[selectedServiceKey]?.subServices?.map((sub, i) => (
                                    <FormControlLabel
                                        key={i}
                                        control={<Checkbox checked={selectedSubServicesTmp.includes(sub)} onChange={() => handleSubServiceToggle(sub)} />}
                                        label={sub}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>
                        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <Button onClick={() => setSelectedServiceKey(null)} sx={{ borderRadius: '999px' }}>Retour</Button>
                            <Button variant="contained" sx={{ borderRadius: '999px' }} onClick={handleSaveServiceSelection} disabled={selectedSubServicesTmp.length === 0}>
                                Enregistrer ({selectedSubServicesTmp.length} sélectionnés)
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );

    const renderSelectedServiceDisplay = () => {
        if (!formData.selectedService || !servicesData[formData.selectedService]) return null;
        const service = servicesData[formData.selectedService];
        return (
            <Card sx={{ mb: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: '14px', overflow: 'hidden' }}>
                <CardContent sx={{ overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, minWidth: 0, flex: 1 }}>
                            <img src={service.icon} alt={service.name} style={{ height: 56, flexShrink: 0 }}
                                 onError={(e) => { e.target.src = '/assets/images/icon/default.png'; }} />
                            <Box sx={{ minWidth: 0, width: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-word', fontSize: { xs: '1rem', sm: '1.25rem' } }} gutterBottom>
                                    {service.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">Compétences :</Typography>
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {formData.subServicesSelected.map((sub, i) => (
                                        <Chip key={i} label={sub} size="small" sx={{ maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal', wordBreak: 'break-word' } }} />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                        <IconButton onClick={handleRemoveSelectedService} color="error" sx={{ flexShrink: 0 }} disabled={isSubmitting}>
                            <Delete />
                        </IconButton>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const handleLanguageSelectChange = (e) => {
        const lang = e.target.value;
        if (lang && !formData.languages.includes(lang)) {
            setFormData(prev => ({ ...prev, languages: [...prev.languages, lang] }));
        }
    };
    const handleRemoveLanguage = (lang) => {
        setFormData(prev => ({ ...prev, languages: prev.languages.filter(l => l !== lang) }));
    };

    const renderProfessionalDetailsStep = () => (
        <Fade in timeout={1000}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <SectionCard icon={<Work />} title="Service" required>
                    {formData.selectedService
                        ? renderSelectedServiceDisplay()
                        : (
                            <Button variant="outlined" startIcon={<Add />} onClick={handleOpenServiceModal}
                                    sx={{ mb: 1, borderRadius: '999px', textTransform: 'none' }} disabled={isSubmitting}>
                                Ajouter un service
                            </Button>
                        )}
                    {errors.serviceId && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.serviceId}</Typography>}
                    {renderServiceSelectionModal()}
                </SectionCard>

                <SectionCard icon={<LanguageIcon />} title="Langues parlées" required>
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {formData.languages.map((lang, i) => (
                            <Chip key={i} label={lang} onDelete={() => handleRemoveLanguage(lang)} deleteIcon={<Cancel />} icon={<LanguageIcon />} sx={{ m: 0.5 }} />
                        ))}
                    </Box>
                    <TextField
                        fullWidth select label="Ajouter une langue" value=""
                        onChange={handleLanguageSelectChange} disabled={isSubmitting}
                        sx={{ ...fieldSx, maxWidth: 400 }}
                    >
                        <MenuItem value=""><em>Sélectionnez une langue</em></MenuItem>
                        {AVAILABLE_LANGUAGES.filter(l => !formData.languages.includes(l)).map((lang, i) => (
                            <MenuItem key={i} value={lang}>{lang}</MenuItem>
                        ))}
                    </TextField>
                    {errors.languages && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.languages}</Typography>}
                </SectionCard>

                <SectionCard icon={<Person />} title="Préférences de travail" required>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                        <FormControlLabel
                            control={<Switch checked={formData.availabilityStatus} onChange={(e) => setFormData(prev => ({ ...prev, availabilityStatus: e.target.checked }))} disabled={isSubmitting} />}
                            label="Disponible immédiatement"
                        />
                        <FormControl fullWidth error={!!errors.yearsOfExperience}>
                            <InputLabel>Années d'expérience <span style={{ color: 'red' }}>*</span></InputLabel>
                            <Select
                                value={formData.yearsOfExperience}
                                onChange={(e) => setFormData(prev => ({ ...prev, yearsOfExperience: e.target.value }))}
                                onBlur={handleBlur}
                                name="yearsOfExperience"
                                disabled={isSubmitting}
                                label={<>Années d'expérience <span style={{ color: 'red' }}>*</span></>}
                            >
                                {YEARS_OF_EXPERIENCE_OPTIONS.map(y => (
                                    <MenuItem key={y} value={y}>{y} {y > 1 ? 'ans' : 'an'}</MenuItem>
                                ))}
                            </Select>
                            {errors.yearsOfExperience && <Typography color="error" variant="caption" sx={{ ml: 2 }}>{errors.yearsOfExperience}</Typography>}
                        </FormControl>
                    </Box>
                </SectionCard>

                <SectionCard icon={<Info />} title="À propos de vous" required>
                    <TextField
                        fullWidth label="À propos de vous" name="about" value={formData.about} onChange={handleChange} onBlur={handleBlur}
                        error={!!errors.about} helperText={errors.about} multiline rows={4} disabled={isSubmitting} autoComplete="off"
                        sx={fieldSx}
                    />
                </SectionCard>

                <SectionCard icon={<Description />} title="CV" required>
                    {renderFileUploadZone('cv', 'CV', { accept: ACCEPTED_DOC_ATTR, required: true })}
                    {errors.cv && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.cv}</Typography>}
                </SectionCard>

                <SectionCard icon={<Groups />} title="Contacts d'urgence" required>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Contact 1</Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <TextField
                                    fullWidth label={<>Nom <span style={{ color: 'red' }}>*</span></>} name="contact1Name"
                                    value={formData.contact1Name} onChange={handleChange} onBlur={handleBlur}
                                    error={!!errors.contact1Name} helperText={errors.contact1Name} disabled={isSubmitting} autoComplete="off"
                                    InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: '#153247' }} /></InputAdornment> }}
                                    sx={fieldSx}
                                />
                                <TextField
                                    fullWidth label="Relation" name="contact1Relationship"
                                    value={formData.contact1Relationship} onChange={handleChange} disabled={isSubmitting} autoComplete="off"
                                    sx={fieldSx}
                                />
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                                    Téléphone <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <PhoneNumberInput
                                    value={formData.contact1Phone}
                                    onChange={(value) => setFormData(prev => ({ ...prev, contact1Phone: value }))}
                                    onValidation={(error) => setErrors(prev => ({ ...prev, contact1Phone: error }))}
                                    disabled={isSubmitting}
                                    defaultCountry="CM"
                                />
                            </Box>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Contact 2</Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <TextField
                                    fullWidth label={<>Nom <span style={{ color: 'red' }}>*</span></>} name="contact2Name"
                                    value={formData.contact2Name} onChange={handleChange} onBlur={handleBlur}
                                    error={!!errors.contact2Name} helperText={errors.contact2Name} disabled={isSubmitting} autoComplete="off"
                                    InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: '#153247' }} /></InputAdornment> }}
                                    sx={fieldSx}
                                />
                                <TextField
                                    fullWidth label="Relation" name="contact2Relationship"
                                    value={formData.contact2Relationship} onChange={handleChange} disabled={isSubmitting} autoComplete="off"
                                    sx={fieldSx}
                                />
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                                    Téléphone <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <PhoneNumberInput
                                    value={formData.contact2Phone}
                                    onChange={(value) => setFormData(prev => ({ ...prev, contact2Phone: value }))}
                                    onValidation={(error) => setErrors(prev => ({ ...prev, contact2Phone: error }))}
                                    disabled={isSubmitting}
                                    defaultCountry="CM"
                                />
                            </Box>
                        </Box>
                    </Box>
                </SectionCard>

                {navButtons(nextStep)}
            </Box>
        </Fade>
    );

    const renderProfessionalDocumentsStep = () => (
        <Fade in timeout={1000}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <SectionCard icon={<CameraAlt />} title="Photo de profil" required>
                    {renderFileUploadZone('profilePhoto', 'Photo de profil', { accept: ACCEPTED_IMAGE_ATTR, required: true, imageOnly: true })}
                    {errors.profilePhoto && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.profilePhoto}</Typography>}
                </SectionCard>

                <SectionCard icon={<Badge />} title="Pièce d'identité" required>
                    <FormControl fullWidth error={!!errors.identificationDocumentType} sx={{ mb: 3 }}>
                        <InputLabel>Type de document</InputLabel>
                        <Select
                            value={formData.identificationDocumentType}
                            onChange={handleIdentificationTypeChange}
                            onBlur={handleBlur}
                            name="identificationDocumentType"
                            disabled={isSubmitting}
                            label="Type de document"
                        >
                            {PROFESSIONAL_DOCUMENT_TYPE_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                        {errors.identificationDocumentType && <Typography color="error" variant="caption" sx={{ ml: 2 }}>{errors.identificationDocumentType}</Typography>}
                    </FormControl>

                    <TextField
                        fullWidth label="Nom du document" value={formData.identificationDocumentName}
                        disabled sx={{ ...fieldSx, mb: 3 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 3 }}>
                        <TextField
                            fullWidth label="Date de délivrance" name="identificationIssueDate" type="date"
                            value={formData.identificationIssueDate} onChange={handleChange} onBlur={handleBlur}
                            error={!!errors.identificationIssueDate} helperText={errors.identificationIssueDate}
                            disabled={isSubmitting} InputLabelProps={{ shrink: true }} sx={fieldSx}
                        />
                        <TextField
                            fullWidth label="Date d'expiration" name="identificationExpiryDate" type="date"
                            value={formData.identificationExpiryDate} onChange={handleChange} onBlur={handleBlur}
                            error={!!errors.identificationExpiryDate} helperText={errors.identificationExpiryDate}
                            disabled={isSubmitting} InputLabelProps={{ shrink: true }} sx={fieldSx}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', mb: 3 }}>
                        <TextField
                            fullWidth label="Autorité émettrice" name="identificationIssuingAuthority"
                            value={formData.identificationIssuingAuthority} onChange={handleChange} onBlur={handleBlur}
                            disabled={isSubmitting} sx={fieldSx}
                        />
                        <CountryInput
                            value={formData.identificationCountry}
                            onChange={(value) => setFormData(prev => ({ ...prev, identificationCountry: value }))}
                            onValidation={(error) => setErrors(prev => ({ ...prev, identificationCountry: error }))}
                            disabled={isSubmitting}
                            required={true}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {renderFileUploadZone('identificationFrontDoc', 'Document (recto)', { accept: ACCEPTED_DOC_ATTR, required: true })}
                        {errors.identificationFrontDoc && <Typography color="error" variant="caption">{errors.identificationFrontDoc}</Typography>}
                        {renderFileUploadZone('identificationBackDoc', 'Document (verso)', { accept: ACCEPTED_DOC_ATTR, required: false })}
                    </Box>
                </SectionCard>

                <SectionCard icon={<MapIcon />} title="Plan de localisation" required>
                    {renderFileUploadZone('locationPlan', 'Plan de localisation', { accept: ACCEPTED_DOC_ATTR, required: true })}
                    {errors.locationPlan && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.locationPlan}</Typography>}
                </SectionCard>

                <Box
                    onClick={() => !isSubmitting && setFormData(prev => ({ ...prev, wantsCriminalRecord: !prev.wantsCriminalRecord }))}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        cursor: isSubmitting ? 'default' : 'pointer',
                        borderRadius: '16px',
                        padding: { xs: '18px', sm: '22px' },
                        border: '2px solid',
                        borderColor: formData.wantsCriminalRecord ? '#1565c0' : '#f8b400',
                        background: formData.wantsCriminalRecord
                            ? 'linear-gradient(135deg, #e8f1fd 0%, #f0eafd 100%)'
                            : 'linear-gradient(135deg, #fff8e6 0%, #fff2d6 100%)',
                        boxShadow: formData.wantsCriminalRecord
                            ? '0 4px 18px rgba(21,101,192,0.18)'
                            : '0 4px 18px rgba(248,180,0,0.25)',
                        transition: 'all 0.25s ease',
                    }}
                >
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                        background: formData.wantsCriminalRecord ? '#1565c0' : '#f8b400',
                        color: '#fff', boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                    }}>
                        {formData.wantsCriminalRecord ? <CheckCircle /> : <Info />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1a202c', fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                            Je souhaite soumettre mon Bulletin n°3 du Casier judiciaire
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
                            {formData.wantsCriminalRecord
                                ? "Activé — vous devez fournir toutes les informations et documents du casier judiciaire ci-dessous."
                                : "Facultatif : activez cette option pour soumettre votre casier judiciaire maintenant."}
                        </Typography>
                    </Box>
                    <Switch
                        checked={formData.wantsCriminalRecord}
                        onChange={(e) => setFormData(prev => ({ ...prev, wantsCriminalRecord: e.target.checked }))}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isSubmitting}
                        sx={{ flexShrink: 0, transform: 'scale(1.3)', mr: 0.5 }}
                    />
                </Box>

                <Collapse in={formData.wantsCriminalRecord}>
                    <SectionCard icon={<Gavel />} title="Bulletin n°3 du Casier judiciaire" required={formData.wantsCriminalRecord}>
                        <TextField
                            fullWidth label="Nom du document" value="Bulletin n°3 du Casier judiciaire" disabled sx={{ ...fieldSx, mb: 3 }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 3 }}>
                            <TextField
                                fullWidth label="Date de délivrance" name="criminalRecordIssueDate" type="date"
                                value={formData.criminalRecordIssueDate} onChange={handleChange} onBlur={handleBlur}
                                error={!!errors.criminalRecordIssueDate} helperText={errors.criminalRecordIssueDate}
                                disabled={isSubmitting} InputLabelProps={{ shrink: true }} sx={fieldSx}
                            />
                            <TextField
                                fullWidth label="Date d'expiration" name="criminalRecordExpiryDate" type="date"
                                value={formData.criminalRecordExpiryDate} onChange={handleChange} onBlur={handleBlur}
                                error={!!errors.criminalRecordExpiryDate} helperText={errors.criminalRecordExpiryDate}
                                disabled={isSubmitting} InputLabelProps={{ shrink: true }} sx={fieldSx}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', mb: 3 }}>
                            <TextField
                                fullWidth label="Autorité émettrice" name="criminalRecordIssuingAuthority"
                                value={formData.criminalRecordIssuingAuthority} onChange={handleChange} onBlur={handleBlur}
                                error={!!errors.criminalRecordIssuingAuthority} helperText={errors.criminalRecordIssuingAuthority}
                                disabled={isSubmitting} sx={fieldSx}
                            />
                            <CountryInput
                                value={formData.criminalRecordCountry}
                                onChange={(value) => setFormData(prev => ({ ...prev, criminalRecordCountry: value }))}
                                onValidation={(error) => setErrors(prev => ({ ...prev, criminalRecordCountry: error }))}
                                disabled={isSubmitting}
                                required={formData.wantsCriminalRecord}
                            />
                        </Box>

                        {renderFileUploadZone('criminalRecordFrontDoc', 'Document (recto)', { accept: ACCEPTED_DOC_ATTR, required: formData.wantsCriminalRecord })}
                        {errors.criminalRecordFrontDoc && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors.criminalRecordFrontDoc}</Typography>}

                        {renderFileUploadZone('criminalRecordBackDoc', 'Document (verso)', { accept: ACCEPTED_DOC_ATTR, required: false })}
                    </SectionCard>
                </Collapse>

                {navButtons(nextStep)}
            </Box>
        </Fade>
    );

    const renderPasswordHelperText = () => {
        if (!formData.password) return errors.password || '';
        const unmetRules = getPasswordRules(formData.password);
        if (unmetRules.length === 0) return '';

        return (
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'error.main', mt: 0.5 }}>
                <Typography variant="caption" component="li" sx={{ listStyle: 'none', fontWeight: 700, mb: 0.5 }}>
                    Le mot de passe doit contenir :
                </Typography>
                {unmetRules.map((rule, i) => (
                    <Typography key={i} variant="caption" component="li" sx={{ lineHeight: 1.4 }}>
                        {rule}
                    </Typography>
                ))}
            </Box>
        );
    };

    const renderConnexionStep = () => (
        <Fade in timeout={1000}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                    fullWidth
                    label={<>Nom d'utilisateur <span style={{ color: 'red' }}>*</span></>}
                    name="username" value={formData.username} onChange={handleChange} onBlur={handleBlur}
                    error={!!errors.username} helperText={errors.username || ''} disabled={isSubmitting} autoComplete="off"
                    InputProps={{ startAdornment: <InputAdornment position="start"><AccountCircle sx={{ color: '#153247' }} /></InputAdornment> }}
                    sx={fieldSx}
                />

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                        fullWidth
                        label={<>Mot de passe <span style={{ color: 'red' }}>*</span></>}
                        name="password" type={showPassword ? "text" : "password"} value={formData.password}
                        onChange={handleChange} onBlur={handleBlur}
                        error={!!errors.password}
                        helperText={renderPasswordHelperText()}
                        disabled={isSubmitting} autoComplete="new-password"
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#153247' }} /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={fieldSx}
                    />
                    <TextField
                        fullWidth
                        label={<>Confirmer le mot de passe <span style={{ color: 'red' }}>*</span></>}
                        name="passwordConfirm" type={showPasswordConfirm ? "text" : "password"} value={formData.passwordConfirm}
                        onChange={handleChange} onBlur={handleBlur} error={!!errors.passwordConfirm} helperText={errors.passwordConfirm}
                        disabled={isSubmitting} autoComplete="new-password"
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#153247' }} /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} edge="end">
                                        {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={fieldSx}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                    <Button
                        variant="outlined" onClick={prevStep} disabled={isSubmitting} startIcon={<ArrowBack />}
                        sx={{
                            borderColor: '#ff6347', color: '#ff6347',
                            '&:hover': { backgroundColor: '#ff6347', color: 'white', borderColor: '#ff4500' },
                            minWidth: '120px', borderRadius: '25px',
                            '&.Mui-disabled': { borderColor: '#ccc', color: '#999' }
                        }}
                    >
                        Retour
                    </Button>
                    <Button
                        type="submit" variant="contained" onClick={handleSubmit}
                        disabled={isSubmitting}
                        endIcon={isSubmitting ? null : <Send />}
                        sx={{ backgroundColor: '#1d8d36', '&:hover': { backgroundColor: 'rgba(12,118,237,0.78)' }, minWidth: '150px', borderRadius: '25px' }}
                    >
                        {isSubmitting ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '18px' }} />
                                Envoi en cours...
                            </Box>
                        ) : 'Soumettre'}
                    </Button>
                </Box>
            </Box>
        </Fade>
    );

    const renderFormStep = () => {
        switch (stepLabel) {
            case 'Type de Profil': return renderProfileTypeStep();
            case 'Informations Personnelles': return renderPersonalStep();
            case 'Adresse': return renderAddressStep();
            case 'Identification': return renderClientIdentificationStep();
            case 'Détails Professionnels': return renderProfessionalDetailsStep();
            case 'Documents': return renderProfessionalDocumentsStep();
            case 'Connexion': return renderConnexionStep();
            default: return null;
        }
    };

    return (
        <>
            <LoadingSpinner isVisible={isLoading} loadingText="Chargement de la page..." />

            <div style={{
                backgroundImage: `url('assets/images/banner/bg-register.jpg')`,
                backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
                minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px'
            }}>
                <Box sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: 4, borderRadius: 3, width: '100%',
                    maxWidth: { xs: '95%', md: step === 0 ? '1000px' : '780px' },
                    margin: '20px auto', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)', backdropFilter: 'blur(10px)', transition: 'all 0.3s ease'
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Link to="/">
                            <img src='/assets/images/logo/logo.png' alt="Logo"
                                 style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid rgb(23 50 71)' }}
                                 onError={(e) => { e.target.src = '/assets/images/logo/logo.png'; }} />
                        </Link>
                    </div>

                    {step > 0 && (
                        <Box sx={{ mb: 4, overflowX: 'auto' }}>
                            <Stepper activeStep={step - 1} alternativeLabel>
                                {steps.slice(1).map((label, index) => (
                                    <Step key={label}>
                                        <StepLabel
                                            sx={{
                                                '& .MuiStepLabel-label': {
                                                    color: step > index + 1 ? '#153247' : 'inherit',
                                                    fontWeight: step === index + 1 ? 'bold' : 'normal'
                                                },
                                                '& .MuiStepIcon-root': {
                                                    color: step > index + 1 ? '#1fbf0d' : 'inherit',
                                                    '&.Mui-active': { color: '#153247' }
                                                }
                                            }}
                                        >
                                            {label}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>
                    )}

                    <Typography variant="h4" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: '#333' }}>
                        {steps[step]}
                    </Typography>

                    <form onSubmit={step === lastStep ? handleSubmit : (e) => e.preventDefault()} autoComplete="off">
                        {renderFormStep()}
                    </form>

                    {step === 0 && (
                        <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <Typography variant="body1">
                                Vous avez déjà un compte ?{' '}
                                <Link to="/login" style={{ color: '#1976d2', fontWeight: 'bold', textDecoration: 'none' }}>
                                    Connexion
                                </Link>
                            </Typography>
                        </Box>
                    )}
                </Box>

                <style jsx>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </>
    );
};

export default RegisterPage;